import React, { useCallback, useEffect, useMemo, useState } from "react";
import BuildInfoModal from "./BuildInfoModal";
import { fetchBuildInfo } from "../api/buildInfo";
import { getLastTraceparent } from "../telemetry";

const isEditableTarget = (target) => {
  if (!target) return false;
  const el = target;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || el.isContentEditable;
};

/**
 * Global "debug/build info" modal opened via keyboard shortcut.
 * Default shortcut: Ctrl/⌘ + Alt + Shift + D (chosen to avoid common browser conflicts).
 */
const BuildInfoShortcut = () => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buildInfo, setBuildInfo] = useState(null);

  const open = useCallback(() => setShow(true), []);
  const close = useCallback(() => setShow(false), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Snapshot the last traceparent from the PREVIOUS request before we make buildInfo call.
      const lastTraceparent = getLastTraceparent();
      const info = await fetchBuildInfo();
      setBuildInfo({ ...info, traceparent: lastTraceparent });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open (and refresh each time it opens).
  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const shortcut = useMemo(
    () => ({
      code: "KeyD",
      requireShift: true,
      requireAlt: true,
      requireCtrlOrMeta: true,
    }),
    []
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      if (isEditableTarget(e.target)) return;

      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      const matches =
        e.code === shortcut.code &&
        (!!e.shiftKey === shortcut.requireShift) &&
        (!!e.altKey === shortcut.requireAlt) &&
        (!shortcut.requireCtrlOrMeta || ctrlOrMeta);

      if (!matches) return;

      // Prevent browser/OS from handling (best effort).
      e.preventDefault();
      e.stopPropagation();
      open();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [open, shortcut]);

  return (
    <BuildInfoModal
      show={show}
      onHide={close}
      loading={loading}
      error={error}
      buildInfo={buildInfo}
      onRefresh={load}
    />
  );
};

export default BuildInfoShortcut;


